/* RESPONSIBLE TEAM: team-data-foundations */

export const SHOPIFY_URL_PLACEHOLDER = 'https://www.shopifystore.com';

export const MCP_SERVER_AUTHENTICATION_TYPES = {
  oauth: 'oauth',
  token: 'token',
  none: 'none',
} as const;

export type McpAuthenticationType =
  (typeof MCP_SERVER_AUTHENTICATION_TYPES)[keyof typeof MCP_SERVER_AUTHENTICATION_TYPES];

export interface McpTemplateDefinition {
  name: string;
  url?: string;
  authentication: McpAuthenticationType;
  icon: string;
  listIcon?: string;
  descriptionKey: string;
  shouldDisplay?: boolean;
  id: string;
}

/* eslint-disable @intercom/intercom/no-bare-strings */
export const MCP_TEMPLATES: Record<string, McpTemplateDefinition> = {
  stripe: {
    id: 'stripe',
    name: 'Stripe',
    url: 'https://mcp.stripe.com',
    authentication: MCP_SERVER_AUTHENTICATION_TYPES.token,
    icon: 'mcp/stripe',
    listIcon: 'mcp/stripe-list',
    descriptionKey: 'workflow-connector.actions.mcp-actions.template-list.stripe.description',
  },
  linear: {
    id: 'linear',
    name: 'Linear',
    url: 'https://mcp.linear.app/sse',
    authentication: MCP_SERVER_AUTHENTICATION_TYPES.oauth,
    icon: 'mcp/linear',
    listIcon: 'mcp/linear-list',
    descriptionKey: 'workflow-connector.actions.mcp-actions.template-list.linear.description',
  },
  'shopify-storefront': {
    id: 'shopify-storefront',
    name: 'Shopify',
    authentication: MCP_SERVER_AUTHENTICATION_TYPES.none,
    icon: 'mcp/shopify',
    listIcon: 'mcp/shopify-list',
    descriptionKey:
      'workflow-connector.actions.mcp-actions.template-list.shopify.storefront-description',
  },
} as const;
/* eslint-enable @intercom/intercom/no-bare-strings */

export const SUPPORTED_MCP_TEMPLATES = Object.values(MCP_TEMPLATES).map(
  (template) => template.name,
);

export type SupportedMcpTemplate = (typeof SUPPORTED_MCP_TEMPLATES)[number];

export function getMcpTemplateByName(name: string): McpTemplateDefinition | undefined {
  return Object.values(MCP_TEMPLATES).find((template) => template.name === name);
}

export function isSupportedMcpTemplate(serverName: string): serverName is SupportedMcpTemplate {
  return SUPPORTED_MCP_TEMPLATES.includes(serverName);
}

export function getAllMcpTemplates(): McpTemplateDefinition[] {
  return Object.values(MCP_TEMPLATES);
}

export function getIconForMcpServer(serverName: string): string {
  return getMcpTemplateByName(serverName)?.icon ?? 'mcp/custom';
}

export function getListIconForMcpServer(serverName: string, darkModeEnabled: boolean): string {
  let template = getMcpTemplateByName(serverName);
  if (template?.listIcon) {
    return template.listIcon;
  }

  return darkModeEnabled ? 'mcp/custom-list-dark' : 'mcp/custom-list-light';
}
